import { useState } from 'react';
import { Loader2, Send, Mail, Phone, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { Reveal, Eyebrow } from './Reveal';
import { CONTACT } from '../constants/site';
import { api } from '../App';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', business: '', budget: '', message: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/inquiries', { ...form, ref: sessionStorage.getItem('gslab_ref') || '' });
      toast.success('Request received — Gabe will reply within one business day.');
      setForm({ name: '', email: '', business: '', budget: '', message: '' });
    } catch {
      toast.error('Could not send. Please email us directly instead.');
    } finally {
      setBusy(false);
    }
  };

  const inputCls = 'w-full bg-[#0B1528]/70 border border-slate-700/60 focus:border-amber-500/60 rounded-xl px-4 py-3 text-sm text-slate-200 outline-none transition-colors placeholder:text-slate-600';

  return (
    <section id="contact" data-testid="contact-section" className="py-24 sm:py-32 bg-[#0B1528]/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid lg:grid-cols-2 gap-14">
        <Reveal>
          <Eyebrow>Let's Build</Eyebrow>
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl">
            Your digital sales engine <span className="text-gradient-gold">starts here.</span>
          </h2>
          <div className="mt-8 space-y-6">
            <div>
              <h3 className="font-display font-semibold text-lg text-amber-300">Free Website & Mobile Review</h3>
              <p className="text-sm text-slate-400 mt-1 leading-relaxed">
                We'll test your current site's mobile responsiveness and pinpoint exactly where prospective customers drop off.
              </p>
            </div>
            <div>
              <h3 className="font-display font-semibold text-lg text-amber-300">Custom Automation Plan</h3>
              <p className="text-sm text-slate-400 mt-1 leading-relaxed">
                See exactly how an interactive quote estimator or booking tool can save you 5+ hours of phone tag each week.
              </p>
            </div>
          </div>
          <div className="mt-10 space-y-3 text-sm">
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="flex items-center gap-3 text-slate-300 hover:text-amber-300 transition-colors">
              <Mail size={16} className="text-amber-400" /> {CONTACT.email}
            </a>
            <a href={`tel:${CONTACT.phone}`} data-testid="contact-phone-link" className="flex items-center gap-3 text-slate-300 hover:text-amber-300 transition-colors">
              <Phone size={16} className="text-amber-400" /> {CONTACT.phone}
            </a>
            <span className="flex items-center gap-3 text-slate-300">
              <Globe size={16} className="text-amber-400" /> {CONTACT.domain}
            </span>
          </div>
        </Reveal>

        <Reveal i={1}>
          <form onSubmit={submit} className="gold-ring rounded-3xl bg-[#0F1C33]/70 p-8 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="contact-form-name-input" required placeholder="Your name" value={form.name} onChange={set('name')} className={inputCls} />
              <input data-testid="contact-form-email-input" required type="email" placeholder="Email address" value={form.email} onChange={set('email')} className={inputCls} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="contact-form-business-input" placeholder="Business name" value={form.business} onChange={set('business')} className={inputCls} />
              <select data-testid="contact-form-budget-select" value={form.budget} onChange={set('budget')} className={inputCls}>
                <option value="">Budget range…</option>
                <option value="under-3k">Under $3,000</option>
                <option value="3k-5k">$3,000 – $5,000</option>
                <option value="5k-plus">$5,000+</option>
                <option value="subscription">Monthly plan first</option>
              </select>
            </div>
            <textarea
              data-testid="contact-form-message-input"
              required
              rows={5}
              placeholder="Tell us about your business and what you want your website to do…"
              value={form.message}
              onChange={set('message')}
              className={inputCls}
            />
            <button
              data-testid="contact-form-submit-button"
              type="submit"
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#D4AF37] via-[#F3C64F] to-[#D4AF37] text-[#050B14] font-bold rounded-full px-6 py-3.5 hover:shadow-[0_0_28px_rgba(212,175,55,0.4)] transition-shadow duration-300 disabled:opacity-60"
            >
              {busy ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
              Request My Free Review
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
